import React, { useContext, useEffect, useState } from 'react';
import { parseInt } from 'lodash';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import BoardsCtx from '../../../../context/boardsContext/boardsCtx';
import FilterCtx from '../../../../context/filterContext/filterCtx';
import { customise, fetchNotes, getBoard, getBoardObjectPeriods } from '../../../services/analysisContentService';
import useBoardContentObject from '../../hooks/useBoardContentObject';
import { POPUP } from '../../../../redux/types';
import { getHomePage } from '../../../services/homeService';
import { useDispatch } from 'react-redux';
import useTranslate from '../../../../common/hooks/useTranslate';
import { moveItem } from '../../../../common/utility/moveItem';

import { IBoardObject } from '../../types/IBoardObject';
import { ITimePeriod } from '../../types/ITimePeriod';
import { ICurrency } from '../../types/ICurrency';
import { IBoardNote } from '../../types/IBoardNote';
import { IBoard } from '../../types/IBoard';
import { IBoardWidget } from '../../types/IBoardWidget';
import { IPropertyData } from '../../types/IPropertyData';
import { IBoardScenario } from '../../types/IBoardScenario';
import { IBoardFiltersValue } from '../../../../context/boardsContext/boardsContext';

import ComponentLoader from '../../../ui/loaders/componentLoader/componentLoader';
import CustomisationGuide from '../../components/customisationGuide/customisationGuide';
import BoardsDropdown from '../../components/boardsDropdown/boardsDropdown';
import ChartWidget from '../../components/widget/chart/chartWidget';
import SummaryWidget from '../../components/widget/summary/summaryWidget';
import MapWidget from '../../components/widget/map/mapWidget';
import GridWidget from '../../components/widget/grid/gridWidget';
import TreelistWidget from '../../components/widget/treelist/treelistWidget';
import DocumentWidget from '../../components/widget/document/documentWidget';

import './boardProfile.scss';

const BoardProfile = () => {
  const [boardInfo, setBoardInfo] = useState<IBoard>();
  const [loading, setLoading] = useState<boolean>(true);
  const [objects, setObjects] = useState<IBoardObject[]>([]);
  const [periods, setPeriods] = useState<ITimePeriod[]>([]);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [scenarios, setScenarios] = useState<IBoardScenario[]>([]);
  const [selectedObject, setSelectedObject] = useState<IBoardObject>();
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency>();
  const [selectedPeriod, setSelectedPeriod] = useState<ITimePeriod>();
  const [notes, setNotes] = useState<IBoardNote[]>([]);
  const [suppressLocal, setSupressLocal] = useState<boolean>(false);
  const [reset, setReset] = useState<boolean>(false);
  const [initialWidgetsForCancel, setInitialWidgetsForCancel] = useState<IBoardWidget[]>([]);
  const [initialWidgets, setInitialWidgets] = useState<IBoardWidget[]>([]);
  const [widgets, setWidgets] = useState<IBoardWidget[]>([]);
  const [templateProperties, setTemplateProperties] = useState<IPropertyData[]>([]);
  const [initialWidgetsVisible, setInitialWidgetsVisible] = useState<IBoardWidget[]>([]);
  const [homeContent, setHomeContent] = useState<any>();
  const [allNotes, setAllNotes] = useState<IBoardNote[]>([]);
  const [loadingObject, setLoadingObject] = useState<boolean>(false);
  const [customisation, setCustomisation] = useState<boolean>(false);

  const boardsCtx = useContext(BoardsCtx);
  const filterCtx = useContext(FilterCtx);
  const params = useParams();
  const { t } = useTranslate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { buildNodeHierarchy } = useBoardContentObject();
  const passedObject = location.state && location.state['data'];
  const contextMatch = boardsCtx.boardFiltersValues?.find((b) => b.boardId === parseInt(params.boardId) && b.investorId === parseInt(params.investorId));
  const boards = useOutletContext();

  const renderWidgets = (board: IBoard) => {
    setBoardInfo(board);

    if (board.widgets) {
      setInitialWidgets(board.widgets.map((widgetItem) => {
          return {...widgetItem, investorObjectId: parseInt(params.investorId) };
      }));

      const widgetData: IBoardWidget[] = board.userContent
        ? board.userContent.widgets.map((widget) => {
          let widgetObject: IBoardWidget = {};
          board.widgets.map((widgetItem) => {
            if (widgetItem.id === widget.widgetId) {
              widgetObject = { ...widgetItem, investorObjectId: parseInt(params.investorId), visible: widget.visible };
            }
          });
          return { ...widgetObject, investorObjectId: parseInt(params.investorId) };
        })
        : board.widgets.map((widget) => {
          return { ...widget, investorObjectId: parseInt(params.investorId), visible: true };
        });

      setWidgets(widgetData);
      setInitialWidgetsVisible(widgetData);
    }
  };

  const filterNotesByObject = (notes: IBoardNote[], object: IBoardObject) => {
    let objectNotes: IBoardNote[] = [];
    notes.map((note) => {
      if (note.objects) {
        for (let i = 0; i < note.objects.length; i++) {
          if (note.objects[i].contentObjectId === object.id) {
            objectNotes.push(note);
          }
        }
      }
    });
    setNotes(objectNotes);
  };

  const saveCustomisation = () => {
    customise(params.boardId, widgets).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    onCustomisationChange(false);
    setInitialWidgetsVisible(widgets);
  };

  const moveWidget = (widget: IBoardWidget, position: number) => {
    const visibleWidgets = widgets.filter((w) => w.visible === true);
    let movedWidgets = moveItem(visibleWidgets, 'id', widget.id, position);
    widgets.forEach((w) => {
      if (!movedWidgets.find((mw) => mw.id === w.id)) movedWidgets.push(w);
    });
    setWidgets(movedWidgets);
  };

  const handlePinWidget = (w: IBoardWidget) => {
    let pinnedWidgets = widgets.map((widget) => {
      return { ...widget, pinned: [...widget.pinned, w] };
    });
    setWidgets(pinnedWidgets);
  };

  const handleUnpinWidget = (w: IBoardWidget) => {
    let pinnedWidgets = widgets.map((widget) => {
      let pinnedArray = widget.pinned.filter((pinnedWidget) => !(pinnedWidget.investorObjectId === w.investorObjectId && pinnedWidget.objectPath === w.objectPath && pinnedWidget.widgetId === w.widgetId));
      return { ...widget, pinned: pinnedArray };
    });
    setWidgets(pinnedWidgets);
  };

  const removeWidget = (widget: IBoardWidget) => {
    setWidgets(
      widgets.map((w) => {
        if (w.id === widget.id) {
          return { ...w, visible: false };
        } else return w;
      }),
    );
  };

  const generateCurrencies = (object: IBoardObject, scenarios: IBoardScenario[], suppressLocalCurrency: boolean) => {
    let currenciesArray: ICurrency[] = [];
    if (object) {
      object.nodesOfObject.forEach((node) => {
        let scenario = scenarios.find((scenario) => {
          return scenario.id === node.scenarioId;
        });
        if (scenario) {
          scenario.currencies?.forEach((currency) => {
            if (
              !currenciesArray.find((curr) => {
                return curr.currency === currency.currency;
              })
            )
              currenciesArray.push(currency);
          });
        }
      });
    }
    if (suppressLocalCurrency) {
      return currenciesArray.filter((curr) => {
        return curr.currency !== '_';
      });
    } else {
      return currenciesArray;
    }
  };

  const setDropdownsData = (board: IBoard, periods: ITimePeriod[]) => {
    setObjects(board.objects);
    setPeriods(periods);
    setScenarios(board.scenarios || []);
  };

  const changeContext = (contextValue: IBoardFiltersValue) => {
    if (!contextMatch) {
      boardsCtx.setBoardFiltersValues([...boardsCtx.boardFiltersValues, contextValue]);
    } else {
      let index = boardsCtx.boardFiltersValues.findIndex((b) => b.boardId === contextMatch.boardId);
      let boardFiltersCopy = [...boardsCtx.boardFiltersValues];
      boardFiltersCopy[index] = contextValue;
      boardsCtx.setBoardFiltersValues(boardFiltersCopy);
    }
  };

  const onObjectChange = (e) => {
    //setSelectedObject(e.value);
    filterCtx.setGridWidgetFilterState({});
    setLoadingObject(true);

    getBoardObjectPeriods(boardInfo?.id || 0, params.investorId || 0, e.value.id)
      .then((periods) => {
        let focusedPeriod = periods.find((item) => item.id === selectedPeriod?.id);
        if (!focusedPeriod && periods.length > 0)
          focusedPeriod = periods[periods.length - 1];

        const currenciesArray = generateCurrencies(e.value, scenarios, suppressLocal);

        let contextValue: IBoardFiltersValue = {
          boardId: parseInt(params.boardId),
          investorId: parseInt(params.investorId),
          selectedObject: e.value || null,
          selectedCurrency: currenciesArray[0] || null,
          selectedPeriod: focusedPeriod || null,
        };
        changeContext(contextValue);

        setCurrencies(currenciesArray);
        filterNotesByObject(allNotes, e.value);
        setPeriods(periods);
      });
  };

  const onPeriodChange = (e) => {
    let contextValue: IBoardFiltersValue = {
      boardId: parseInt(params.boardId),
      investorId: parseInt(params.investorId),
      selectedObject: selectedObject || null,
      selectedCurrency: selectedCurrency || null,
      selectedPeriod: e.value || null,
    };
    changeContext(contextValue);

    if (e.value && params.boardId && selectedObject) {
      fetchNotes(parseInt(params.boardId), e.value.id)
        .then((notesData) => {
          setAllNotes(notesData);
          filterNotesByObject(notesData, selectedObject);
        })
        .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    }
  };

  const onCurrencyChange = (e) => {
    let contextValue: IBoardFiltersValue = {
      boardId: parseInt(params.boardId),
      investorId: parseInt(params.investorId),
      selectedObject: selectedObject || null,
      selectedCurrency: e.value || null,
      selectedPeriod: selectedPeriod || null,
    };
    changeContext(contextValue);
  };

  const onCustomisationChange = (e) => {
    if (e === true) {
      setInitialWidgetsForCancel([...widgets]);
    }
    setCustomisation(e);
  };

  const onResetChange = (e) => {
    setReset(e);
  };

  const handleCancel = () => {
    setWidgets([...initialWidgetsForCancel]);
  };

  const resetCustomisation = (boardId) => {
    const widgetsReset = initialWidgets.map((widget) => {
      return { ...widget, visible: true };
    });
    customise(boardId, widgetsReset).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
  };

  useEffect(() => {
    setLoading(true);
    filterCtx.setGridWidgetFilterState({});

    if (params.investorId) {
      Promise.all([getBoard(parseInt(params.boardId), parseInt(params.investorId)), getHomePage()])
        .then((res) => {
          let board = res[0];
          let content = res[1];
          if (content) {
            setHomeContent(content);
          }
          let focusedObj: IBoardObject | undefined = undefined;
          if (board.objects) {
            board.objects = board.objects.map((obj) => {
              buildNodeHierarchy(obj.nodes);
              return obj;
            });

            if (params.objectId) {
              const object = board.objects.find((item) => item.objectId === parseInt(params.objectId));
              if (object) focusedObj = object;
              else dispatch({ type: POPUP.SET_SERVER_ERROR, payload: t('BOARDS.NO_OBJECT_ID_ERROR') });
            } else if (passedObject) {
              const object = board.objects.find((item) => item.objectPath === passedObject);
              if (object) focusedObj = object;
            } else focusedObj = board.objects[0];
          }

          return (focusedObj ? getBoardObjectPeriods(board.id || 0, params.investorId, focusedObj.id || 0) : Promise.resolve(board.timePeriods))
            .then((periods) => {
              return (periods.length > 0 ? fetchNotes(board.id, periods[periods.length - 1].id) : Promise.resolve([]))
                .then((notesData) => {
                  renderWidgets(board);

                  if (board.staticProperties) {
                    setTemplateProperties(board.staticProperties);
                  }

                  setSelectedObject(focusedObj);

                  if (board.suppressLocalCurrency) {
                    setSupressLocal(board.suppressLocalCurrency);
                  }

                  if (focusedObj) {
                    filterNotesByObject(notesData, focusedObj);
                    const currenciesArray = generateCurrencies(focusedObj, board.scenarios || [], board.suppressLocalCurrency || false);
                    setCurrencies(currenciesArray);
                    let selectedCurr = contextMatch && contextMatch.selectedCurrency && currenciesArray.find((c) => c.currency === contextMatch.selectedCurrency?.currency) || currenciesArray[0];
                    setSelectedCurrency(selectedCurr);
                  }

                  if (periods.length > 0) {
                    let period = contextMatch && contextMatch.selectedPeriod && periods.find((p) => p.id === contextMatch.selectedPeriod?.id) || periods[periods.length - 1];
                    setSelectedPeriod(period);
                  } else {
                    setSelectedPeriod(null);
                  }

                  setDropdownsData(board, periods);

                  setAllNotes(notesData);

                  setLoading(false);
                });
            });
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
        });
    }
  }, [params.boardId, params.investorId, reset]);

  useEffect(() => {
    let object = contextMatch && contextMatch.selectedObject;
    if (object && objects?.find(o => o.objectId === object.objectId)) setSelectedObject(object);

    let period = contextMatch && contextMatch.selectedPeriod;
    if (period && periods.find(p => p.id === period.id)) setSelectedPeriod(period);

    let currency = contextMatch && contextMatch.selectedCurrency;
    if (currency && currencies.find((c) => c.currency === currency?.currency)) setSelectedCurrency(currency);
  }, [boardsCtx.boardFiltersValues]);

  return (
    <>
      {loading ? (
          <ComponentLoader />
        ) :
        !loadingObject && (!selectedObject || !selectedPeriod || !selectedCurrency) ?
          <h2>{t('BOARDS.NO_DATA_AVAILABLE')}</h2> :
          <>
            <BoardsDropdown
              loading={loading}
              objects={objects}
              periods={periods}
              currencies={currencies}
              selectedObject={selectedObject}
              selectedCurrency={selectedCurrency}
              selectedPeriod={selectedPeriod}
              onObjectChange={onObjectChange}
              onPeriodChange={onPeriodChange}
              onCurrenciesChange={onCurrencyChange}
              onCustomisationChange={onCustomisationChange}
              filteredNotes={notes}
            />
            <div className='boards-content-wrapper'>
              {widgets &&
                widgets.map((widget) => {
                  if (widget.type === 'summary' && widget.visible) {
                    return (
                      <SummaryWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        selectedCurrency={selectedCurrency}
                        boardData={boardInfo}
                        scenarios={scenarios}
                        content={homeContent}
                        customisation={customisation}
                        notes={notes}
                        templateProperties={templateProperties}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handlePinWidget={handlePinWidget}
                      />
                    );
                  }
                  if (widget.type === 'grid' && widget.visible) {
                    return (
                      <GridWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        selectedCurrency={selectedCurrency}
                        boardData={boardInfo}
                        boards={boards}
                        scenarios={scenarios}
                        customisation={customisation}
                        notes={notes}
                        content={homeContent}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handlePinWidget={handlePinWidget}
                      />
                    );
                  }
                  if (widget.type === 'treelist' && widget.visible) {
                    return (
                      <TreelistWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        selectedCurrency={selectedCurrency}
                        boardData={boardInfo}
                        scenarios={scenarios}
                        customisation={customisation}
                        notes={notes}
                        content={homeContent}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handlePinWidget={handlePinWidget}
                      />
                    );
                  }
                  if (widget.type === 'map' && widget.visible) {
                    return (
                      <MapWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        selectedCurrency={selectedCurrency}
                        boardData={boardInfo}
                        scenarios={scenarios}
                        customisation={customisation}
                        notes={notes}
                        content={homeContent}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                      />
                    );
                  }
                  if (widget.type === 'chart' && widget.visible) {
                    return (
                      <ChartWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        selectedCurrency={selectedCurrency}
                        boardData={boardInfo}
                        scenarios={scenarios}
                        customisation={customisation}
                        notes={notes}
                        content={homeContent}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handlePinWidget={handlePinWidget}
                      />
                    );
                  }
                  if (widget.type === 'document' && widget.visible) {
                    return (
                      <DocumentWidget
                        widget={widget}
                        key={widget.id}
                        selectedObject={selectedObject}
                        selectedPeriod={selectedPeriod}
                        customisation={customisation}
                        content={homeContent}
                        moveWidget={moveWidget}
                        removeWidget={removeWidget}
                        handleUnpinWidget={handleUnpinWidget}
                        handlePinWidget={handlePinWidget}
                      />
                    );
                  }
                })}
            </div>
          </>
      }
      <CustomisationGuide customisation={customisation} saveCustomisation={saveCustomisation}
                          handleCustomisation={onCustomisationChange} handleReset={onResetChange}
                          resetCustomisation={resetCustomisation} handleCancel={handleCancel} />
    </>
  );
};

export default BoardProfile;